/* ============================================================
   wsp.js — the storefront shell.

   Catalogue loading, the display dropdowns, club badges, the basket,
   and the small shared helpers every page uses.
   ============================================================ */
(function (W, D) {
  'use strict';

  /* Pages sit at the site root, so the asset base is constant. If the site
     is ever mounted under a sub-path, this is the one line to change. */
  const BASE = '';

  const $  = (sel, root = D) => root.querySelector(sel);
  const $$ = (sel, root = D) => Array.from(root.querySelectorAll(sel));
  const el = (tag, cls, html) => {
    const n = D.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  };
  const esc = s => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const money = n => '£' + Number(n).toFixed(2);
  const param = k => new URLSearchParams(location.search).get(k);
  const slugify = s => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  /* ------------------------------------------------------------------
     Catalogue
     ------------------------------------------------------------------ */
  let _cat = null, _catPromise = null;
  function catalogue() {
    if (_catPromise) return _catPromise;
    /* On Shopify the catalogue is assembled by the theme — design data from
       a theme asset, joined to live prices and variant ids emitted by
       Liquid — and handed over here rather than fetched. Everything
       downstream is identical, so no page code has to know which it got. */
    _catPromise = (W.WSP_CATALOGUE
        ? Promise.resolve(W.WSP_CATALOGUE)
        : fetch(BASE + 'data/catalogue.json')
            .then(r => { if (!r.ok) throw new Error('catalogue ' + r.status); return r.json(); }))
      .then(c => {
        _cat = c;
        c.byslug   = Object.fromEntries(c.products.map(p => [p.slug, p]));
        c.styleById = Object.fromEntries(c.styles.map(s => [s.id, s]));
        c.sizeById  = Object.fromEntries(c.sizes.map(s => [s.id, s]));
        c.frameById = Object.fromEntries(c.frames.map(f => [f.id, f]));
        c.clubBySlug = Object.fromEntries(c.clubs.map(x => [x.slug, x]));
        return c;
      });
    return _catPromise;
  }

  /* The image to show for a product in a given style. Falls back to any
     render we do have — a missing pre-render is a preview gap, not a gap
     in what we can print. */
  function shot(p, styleId, size) {
    const key = size === 'sm' ? 'sm' : 'lg';
    const pv = p.previews[styleId] || p.previews[p.styles.find(s => p.previews[s])] || Object.values(p.previews)[0];
    return pv ? BASE + pv[key] : '';
  }

  /* ------------------------------------------------------------------
     Club badge

     Two forms. Where a crest has been fetched (engine/fetch-crests.mjs) it
     is used HERE, in the browse UI only. Where one has not, the fallback
     is a shield built from the club's colours and initials, so the wall is
     never ragged.

     The crest never reaches the printed product. The print templates read
     club COLOURS from data/clubs.json and nothing else — a badge on
     merchandise is the trademark exposure that file has always warned
     about, and that has not changed.
     ------------------------------------------------------------------ */
  const SHIELD = 'M50 4 L92 16 C92 52 84 78 50 96 C16 78 8 52 8 16 Z';

  function initials(name) {
    const skip = /^(and|the|of|&|hove|city|united|town|wanderers|albion|hotspur|forest|villa|county|rovers|athletic|nassr|al)$/i;
    const words = name.replace(/&/g, ' ').split(/\s+/).filter(w => w && !skip.test(w));
    const use = (words.length ? words : name.split(/\s+/)).slice(0, 3);
    return use.map(w => w[0].toUpperCase()).join('');
  }

  function crest(club, size) {
    if (!club) return '';
    if (club.crest) {
      return `<img class="crest" src="${esc(BASE + club.crest)}" alt="${esc(club.name)}"
                   width="100" height="100" loading="lazy" decoding="async"
                   style="object-fit:contain">`;
    }
    const id = 'c' + Math.random().toString(36).slice(2, 8);
    const { primary: a, secondary: b, ink, kit } = club;
    let fill = `<path d="${SHIELD}" fill="${a}"/>`;

    if (kit === 'stripes') {
      fill += `<g clip-path="url(#${id})">` +
        [16, 34, 52, 70].map(x => `<rect x="${x}" y="0" width="9" height="100" fill="${b}"/>`).join('') +
        `</g>`;
    } else if (kit === 'hoops') {
      fill += `<g clip-path="url(#${id})">` +
        [14, 34, 54, 74].map(y => `<rect x="0" y="${y}" width="100" height="10" fill="${b}"/>`).join('') +
        `</g>`;
    } else if (kit === 'halves') {
      fill += `<g clip-path="url(#${id})"><rect x="50" y="0" width="50" height="100" fill="${b}"/></g>`;
    } else if (kit === 'sash') {
      fill += `<g clip-path="url(#${id})"><rect x="34" y="-20" width="18" height="140" fill="${b}" transform="rotate(-18 50 50)"/></g>`;
    } else if (kit === 'sleeves') {
      fill += `<g clip-path="url(#${id})"><rect x="0" y="0" width="14" height="100" fill="${b}"/>` +
              `<rect x="86" y="0" width="14" height="100" fill="${b}"/></g>`;
    }

    return `<svg class="crest" viewBox="0 0 100 100" role="img" aria-label="${esc(club.name)} colours" focusable="false">
      <defs><clipPath id="${id}"><path d="${SHIELD}"/></clipPath></defs>
      ${fill}
      <path d="${SHIELD}" fill="none" stroke="rgba(255,255,255,.55)" stroke-width="2.5"/>
      <path d="${SHIELD}" fill="none" stroke="rgba(0,0,0,.28)" stroke-width="1"/>
      <text x="50" y="58" text-anchor="middle" fill="${ink}"
            font-family="Archivo, Helvetica, Arial, sans-serif" font-weight="800"
            font-size="${initials(club.name).length > 2 ? 26 : 32}" letter-spacing="0.5"
            style="paint-order:stroke;stroke:rgba(0,0,0,.18);stroke-width:.6px"
      >${esc(initials(club.name))}</text>
    </svg>`;
  }

  /* ------------------------------------------------------------------
     Basket — localStorage only. There is no checkout here: the site is
     built to be lifted into a website builder, which brings its own cart.
     This exists so the customiser has somewhere to put a configured
     print, and so the flow can be demonstrated end to end.
     ------------------------------------------------------------------ */
  const KEY = 'wsp.basket.v1';
  const basket = {
    read() { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; } },
    write(v) { localStorage.setItem(KEY, JSON.stringify(v)); this.paint(); },
    add(line) {
      const v = this.read();
      const same = v.find(l => l.sku === line.sku && l.frame === line.frame && l.dedication === line.dedication);
      if (same) same.qty += line.qty || 1; else v.push(Object.assign({ qty: 1 }, line));
      this.write(v);
      toast(`${line.name} added to bag`);
    },
    remove(i) { const v = this.read(); v.splice(i, 1); this.write(v); },
    count() { return this.read().reduce((n, l) => n + l.qty, 0); },
    total() { return this.read().reduce((n, l) => n + l.price * l.qty, 0); },
    paint() {
      const n = this.count();
      $$('[data-basket-count]').forEach(e => { e.textContent = n; e.hidden = n === 0; });
      const body = $('[data-basket-body]');
      if (!body) return;
      const lines = this.read();
      body.innerHTML = lines.length ? '' : `<p class="small" style="padding:40px 0;color:var(--t3)">Your bag is empty.</p>`;
      lines.forEach((l, i) => {
        const node = el('div', 'line', `
          <div class="line__shot"><img src="${esc(l.image)}" alt=""></div>
          <div style="flex:1;min-width:0">
            <div class="line__name">${esc(l.name)}</div>
            <div class="line__meta">${esc(l.meta)}</div>
            ${l.dedication ? `<div class="line__meta">Engraved: “${esc(l.dedication)}”</div>` : ''}
            <div class="line__foot">
              <span class="small num">${money(l.price)} × ${l.qty}</span>
              <button class="line__rm" type="button">Remove</button>
            </div>
          </div>`);
        $('.line__rm', node).addEventListener('click', () => basket.remove(i));
        body.appendChild(node);
      });
      const t = $('[data-basket-total]');
      if (t) t.textContent = money(this.total());
      const go = $('[data-basket-checkout]');
      if (go) go.disabled = lines.length === 0;
    },
  };

  /* ------------------------------------------------------------------
     Chrome: dropdowns, drawer, disclosures, toast, reveal
     ------------------------------------------------------------------ */
  let openDD = null;
  function closeDD() {
    if (!openDD) return;
    openDD.panel.removeAttribute('data-open');
    openDD.trigger.setAttribute('aria-expanded', 'false');
    $('[data-scrim]')?.removeAttribute('data-open');
    openDD = null;
  }
  function bindDropdowns() {
    $$('[data-dd-trigger]').forEach(trigger => {
      const panel = $('#' + trigger.getAttribute('aria-controls'));
      if (!panel) return;
      const open = () => {
        if (openDD && openDD.panel === panel) return closeDD();
        closeDD();
        panel.setAttribute('data-open', '');
        trigger.setAttribute('aria-expanded', 'true');
        $('[data-scrim]')?.setAttribute('data-open', '');
        openDD = { panel, trigger };
      };
      trigger.addEventListener('click', e => { e.preventDefault(); open(); });
    });
    $('[data-scrim]')?.addEventListener('click', closeDD);
    D.addEventListener('keydown', e => { if (e.key === 'Escape') { closeDD(); closeDrawer(); } });
    D.addEventListener('click', e => {
      if (openDD && !e.target.closest('.dd') && !e.target.closest('[data-dd-trigger]')) closeDD();
    });
  }

  function openDrawer()  { $('[data-drawer]')?.setAttribute('data-open', ''); $('[data-scrim]')?.setAttribute('data-open', ''); }
  function closeDrawer() { $('[data-drawer]')?.removeAttribute('data-open'); if (!openDD) $('[data-scrim]')?.removeAttribute('data-open'); }

  function bindDisclosures(root = D) {
    $$('.disc__head', root).forEach(h => {
      if (h.dataset.bound) return;
      h.dataset.bound = '1';
      h.addEventListener('click', () => {
        const on = h.getAttribute('aria-expanded') === 'true';
        h.setAttribute('aria-expanded', String(!on));
      });
    });
  }

  let toastTimer;
  function toast(msg) {
    let t = $('[data-toast]');
    if (!t) { t = el('div', 'toast'); t.setAttribute('data-toast', ''); D.body.appendChild(t); }
    t.textContent = msg;
    t.setAttribute('data-open', '');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => t.removeAttribute('data-open'), 2600);
  }

  function bindReveal(root = D) {
    const items = $$('.reveal', root).filter(n => !n.dataset.seen);
    if (!items.length) return;
    if (!('IntersectionObserver' in W)) return items.forEach(n => n.dataset.seen = '1');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e, i) => {
        if (!e.isIntersecting) return;
        setTimeout(() => e.target.dataset.seen = '1', Math.min(i * 60, 240));
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    items.forEach(n => io.observe(n));
  }

  /* Fill the nav's club/league dropdown from the catalogue, so the menu is
     never out of step with what is actually for sale. */
  /* URL shapes differ between the static site and the Shopify theme, where
     the same lists have to point at collections and /products/<handle>.
     wsp-theme.js replaces these three; nothing else in the file needs to
     know which storefront it is running on. */
  const ROUTES = {
    club:    cl => `shop.html?club=${cl.slug}`,
    style:   s  => `shop.html?style=${s.id}`,
    product: p  => `product.html?p=${p.slug}`,
  };

  function fillNav(c) {
    const clubCol = $('[data-nav-clubs]');
    if (clubCol) {
      const byLeague = {};
      c.clubs.forEach(cl => (byLeague[cl.league] ??= []).push(cl));
      clubCol.innerHTML = Object.entries(byLeague).map(([league, clubs]) => `
        <div class="dd__col">
          <span class="label">${esc(league)}</span>
          ${clubs.map(cl => `<a class="dd__link" href="${W.WSP.ROUTES.club(cl)}">
             <span style="width:20px;flex:none">${crest(cl)}</span>
             <span>${esc(cl.name)}</span>
             <span class="fine num">${cl.count}</span>
           </a>`).join('')}
        </div>`).join('');
    }
    const styleCol = $('[data-nav-styles]');
    if (styleCol) {
      styleCol.innerHTML = c.styles.map(s => `
        <a class="dd__link" href="${W.WSP.ROUTES.style(s)}">
          <span>${esc(s.name)}</span>
          <span class="fine num">from ${money(s.from)}</span>
        </a>`).join('');
    }
    const playerCol = $('[data-nav-players]');
    if (playerCol) {
      playerCol.innerHTML = c.products.slice(0, 12).map(p => `
        <a class="dd__link" href="${W.WSP.ROUTES.product(p)}">
          <span>${esc(p.name)}</span>
        </a>`).join('');
    }
  }

  function boot() {
    /* A host storefront (the Shopify theme) gets to finish wiring WSP —
       routes, basket, its own data — before anything reads it. Polling for
       WSP from the outside raced the catalogue event and left sections
       reading an undefined WSP.shopify. */
    if (typeof W.WSP_THEME_INIT === 'function') {
      try { W.WSP_THEME_INIT(); } catch (e) { console.error('theme init', e); }
    }
    bindDropdowns();
    bindDisclosures();
    bindReveal();
    basket.paint();
    $$('[data-open-basket]').forEach(b => b.addEventListener('click', openDrawer));
    $$('[data-close-basket]').forEach(b => b.addEventListener('click', closeDrawer));
    $$('[data-year]').forEach(e => e.textContent = new Date().getFullYear());

    catalogue().then(c => {
      fillNav(c);
      D.dispatchEvent(new CustomEvent('wsp:catalogue', { detail: c }));
    }).catch(err => {
      console.error(err);
      const note = $('[data-catalogue-error]');
      if (note) note.hidden = false;
    });
  }

  W.WSP = {
    BASE, ROUTES, $, $$, el, esc, money, param, slugify,
    catalogue, shot, crest, initials, basket, toast,
    bindDisclosures, bindReveal, openDrawer, closeDrawer,
  };

  D.readyState === 'loading' ? D.addEventListener('DOMContentLoaded', boot) : boot();
})(window, document);
